import { z } from 'zod';
import { timestampSchema } from './admin.js';
import { isoDateSchema, projectKeySchema } from './projects.js';
import { taskKeySchema, taskPrioritySchema, taskTypeSchema } from './tasks.js';
import { dueFilterSchema } from './task-list.js';

/** AI-01..08 (ADR-015): the AI assistant for internal users. */

/** Problem `type` URIs of the assistant, so the UI can show specific messages. */
export const assistantProblemTypes = {
  /** AI-07: requests per person and day. */
  dailyLimit: 'urn:pm-tool:problem:assistant-daily-limit',
  /** AI-07: monthly budget of the whole company. */
  budgetExhausted: 'urn:pm-tool:problem:assistant-budget-exhausted',
  /** The model provider did not answer (network, rate limit, outage). */
  providerUnavailable: 'urn:pm-tool:problem:assistant-provider-unavailable',
} as const;

export const ASSISTANT_MAX_INPUT_CHARS = 4000;
/** Days a conversation is kept after its last message (AI-06). */
export const ASSISTANT_RETENTION_DAYS = 30;

// --- HTTP API ----------------------------------------------------------------------------------

/** Page the panel was opened on, as context for the question. */
export const assistantContextSchema = z.object({
  projectKey: projectKeySchema.optional(),
  taskKey: taskKeySchema.optional(),
});
export type AssistantContext = z.infer<typeof assistantContextSchema>;

export const assistantMessageBodySchema = z.object({
  text: z.string().trim().min(1).max(ASSISTANT_MAX_INPUT_CHARS),
  /** Continue a conversation; without it a new one is started. */
  conversationId: z.uuid().optional(),
  context: assistantContextSchema.optional(),
});
export type AssistantMessageBody = z.infer<typeof assistantMessageBodySchema>;

export const assistantMessageSchema = z.object({
  id: z.uuid(),
  role: z.enum(['user', 'assistant']),
  /** Markdown subset: paragraphs, bold, lists, links to app paths. */
  text: z.string(),
  /** What the assistant looked up for this answer (shown as steps). */
  steps: z.array(z.string()),
  /** Proposed changes of this answer (KI-B). */
  actions: z.array(z.lazy(() => assistantActionSchema)),
  createdAt: timestampSchema,
});
export type AssistantMessage = z.infer<typeof assistantMessageSchema>;

export const assistantConversationSummarySchema = z.object({
  id: z.uuid(),
  title: z.string(),
  updatedAt: timestampSchema,
});
export type AssistantConversationSummary = z.infer<typeof assistantConversationSummarySchema>;

export const assistantConversationListSchema = z.object({
  items: z.array(assistantConversationSummarySchema),
});

export const assistantConversationSchema = assistantConversationSummarySchema.extend({
  messages: z.array(assistantMessageSchema),
});
export type AssistantConversation = z.infer<typeof assistantConversationSchema>;

export const assistantUsageSchema = z.object({
  requestsToday: z.number().int(),
  dailyLimit: z.number().int(),
  /** AI-07: no further requests this month for anyone. */
  budgetExhausted: z.boolean(),
});
export type AssistantUsage = z.infer<typeof assistantUsageSchema>;

/**
 * Server-sent events of POST /assistant/messages, one JSON object per `data:` line, in this
 * order: `conversation`, then `step`/`text` as they happen, then `done` or `error`.
 */
export const assistantStreamEventSchema = z.discriminatedUnion('type', [
  z.object({ type: z.literal('conversation'), id: z.uuid(), title: z.string() }),
  z.object({ type: z.literal('step'), label: z.string() }),
  z.object({ type: z.literal('action'), action: z.lazy(() => assistantActionSchema) }),
  z.object({ type: z.literal('text'), delta: z.string() }),
  z.object({ type: z.literal('done'), messageId: z.uuid() }),
  z.object({
    type: z.literal('error'),
    message: z.string(),
    problemType: z.string().optional(),
  }),
]);
export type AssistantStreamEvent = z.infer<typeof assistantStreamEventSchema>;

// --- Tool inputs (validated before every tool call; the model's arguments are untrusted) ---------

/** Exactly one of week, month or from/to (resolvePeriod). */
export const assistantPeriodSchema = z
  .object({
    week: z
      .string()
      .regex(/^\d{4}-W\d{2}$/)
      .optional()
      .describe('ISO week, e.g. 2026-W43 (KW 43 in 2026)'),
    month: z
      .string()
      .regex(/^\d{4}-\d{2}$/)
      .optional()
      .describe('Month, e.g. 2026-10'),
    from: isoDateSchema.optional().describe('Start date YYYY-MM-DD (with to)'),
    to: isoDateSchema.optional().describe('End date YYYY-MM-DD, inclusive (with from)'),
  })
  .strict();

const projectKey = projectKeySchema.describe('Project key, e.g. KUNDA');

export const assistantToolInputSchemas = {
  list_projects: z
    .object({
      includeArchived: z.boolean().optional().describe('Also archived projects'),
      onlyMine: z.boolean().optional().describe('Only projects the user is a member of'),
    })
    .strict(),
  get_project: z.object({ projectKey }).strict(),
  find_tasks: z
    .object({
      projectKey,
      query: z.string().max(200).optional().describe('Text in key, title or description'),
      statuses: z.array(z.string().max(100)).max(20).optional().describe('Status names'),
      assignee: z
        .string()
        .max(200)
        .optional()
        .describe('"me", "none" or a person name (project member)'),
      labels: z.array(z.string().max(100)).max(20).optional().describe('Label names'),
      priorities: z.array(taskPrioritySchema).max(4).optional(),
      type: taskTypeSchema.optional(),
      due: dueFilterSchema.optional().describe('overdue, today, week (this week) or none'),
      dueFrom: isoDateSchema.optional(),
      dueTo: isoDateSchema.optional(),
      includeSubtasks: z.boolean().optional(),
    })
    .strict(),
  search: z
    .object({ query: z.string().min(1).max(200).describe('Text or task key, all projects') })
    .strict(),
  get_task: z.object({ taskKey: taskKeySchema.describe('Task key, e.g. KUNDA-12') }).strict(),
  my_tasks: z.object({}).strict(),
  project_health: z.object({}).strict(),
  get_plan: z.object({ projectKey }).strict(),
  list_job_roles: z.object({}).strict(),
  list_areas: z.object({}).strict(),
  list_skills: z.object({}).strict(),
  find_people_by_skill: z
    .object({
      skills: z
        .array(z.string().max(100))
        .max(10)
        .optional()
        .describe('Skill names; people must have all of them, e.g. ["Web"]'),
      area: z.string().max(100).optional().describe('Area name, e.g. Entwicklung'),
      jobRole: z.string().max(100).optional().describe('Job role name, e.g. Projektleitung'),
    })
    .strict(),
  free_capacity: z
    .object({
      period: assistantPeriodSchema,
      jobRole: z.string().max(100).optional().describe('Job role name, e.g. Design'),
      area: z.string().max(100).optional().describe('Area name: people with any role in it'),
      skills: z
        .array(z.string().max(100))
        .max(10)
        .optional()
        .describe('Skill names; only people having all of them'),
      granularity: z.enum(['day', 'week']).optional().describe('Breakdown, default week'),
    })
    .strict(),
  role_capacity: z
    .object({
      period: assistantPeriodSchema,
      area: z.string().max(100).optional().describe('Only the job roles of this area'),
    })
    .strict(),
  project_resources: z.object({ projectKey, period: assistantPeriodSchema }).strict(),
  find_people: z
    .object({ name: z.string().min(1).max(200).describe('Part of a name or e-mail') })
    .strict(),
} as const;

export type AssistantToolName = keyof typeof assistantToolInputSchemas;
export type AssistantToolInput<N extends AssistantToolName> = z.infer<
  (typeof assistantToolInputSchemas)[N]
>;

// --- Actions with preview and confirmation (KI-B, AI-03) ----------------------------------------

export const assistantActionKindSchema = z.enum([
  'create_project',
  'create_tasks',
  'update_task',
  'create_dependency',
  'shift_plan',
]);
export type AssistantActionKind = z.infer<typeof assistantActionKindSchema>;

export const assistantActionStatusSchema = z.enum([
  'pending',
  'confirmed',
  'rejected',
  'expired',
  'failed',
]);

/** Minutes a preview can be confirmed (AI-03). */
export const ASSISTANT_ACTION_TTL_MINUTES = 15;

export const assistantActionProblemTypes = {
  /** The preview expired or was decided already. */
  notPending: 'urn:pm-tool:problem:assistant-action-not-pending',
} as const;

/** A preview card in the chat: what will change, then the outcome. */
export const assistantActionSchema = z.object({
  id: z.uuid(),
  kind: assistantActionKindSchema,
  title: z.string(),
  /** What changes, old → new, one line each. */
  lines: z.array(z.string()),
  /** Consequences to look at before confirming (conflicts, shifted successors). */
  warnings: z.array(z.string()),
  status: assistantActionStatusSchema,
  expiresAt: timestampSchema,
  /** After confirming: where the result is. */
  links: z.array(z.object({ label: z.string(), url: z.string() })),
  /** After a failed confirmation. */
  error: z.string().nullable(),
});
export type AssistantAction = z.infer<typeof assistantActionSchema>;

const dateInput = isoDateSchema.describe('YYYY-MM-DD');

export const assistantActionToolInputSchemas = {
  propose_create_project: z
    .object({
      key: projectKeySchema.describe('2–6 capital letters, unique, e.g. SHOP'),
      name: z.string().min(1).max(200),
      customer: z.string().max(200).optional().describe('Customer organization name'),
      lead: z.string().max(200).optional().describe('Internal person name; default: the user'),
      startDate: dateInput.optional(),
      endDate: dateInput.optional(),
      description: z.string().max(4000).optional(),
    })
    .strict(),
  propose_create_tasks: z
    .object({
      projectKey,
      tasks: z
        .array(
          z
            .object({
              title: z.string().min(1).max(300),
              parent: z
                .string()
                .max(300)
                .optional()
                .describe('Key of an existing task or title of a task in this list'),
              type: taskTypeSchema.optional(),
              status: z.string().max(100).optional().describe('Status name'),
              priority: taskPrioritySchema.optional(),
              assignee: z.string().max(200).optional().describe('Name of one project member'),
              startDate: dateInput.optional(),
              dueDate: dateInput.optional(),
              description: z.string().max(4000).optional(),
            })
            .strict(),
        )
        .min(1)
        .max(30),
    })
    .strict(),
  propose_update_task: z
    .object({
      taskKey: taskKeySchema,
      title: z.string().min(1).max(300).optional(),
      status: z.string().max(100).optional().describe('Status name'),
      priority: taskPrioritySchema.optional(),
      assignee: z
        .string()
        .max(200)
        .nullable()
        .optional()
        .describe('Name of one project member; null = nobody'),
      startDate: dateInput.nullable().optional(),
      dueDate: dateInput.nullable().optional(),
      progress: z.number().int().min(0).max(100).optional(),
    })
    .strict(),
  propose_create_dependency: z
    .object({
      predecessorKey: taskKeySchema,
      successorKey: taskKeySchema,
      type: z.enum(['FS', 'SS', 'FF', 'SF']).optional().describe('Default FS (end → start)'),
      lagDays: z.number().int().min(-250).max(250).optional().describe('Working days'),
    })
    .strict(),
  propose_shift_plan: z
    .object({
      projectKey,
      workingDays: z.number().int().min(-250).max(250).describe('Negative = earlier; 1 week = 5'),
      from: dateInput.optional().describe('Only open tasks from this date on; default: all'),
    })
    .strict(),
} as const;

export type AssistantActionToolName = keyof typeof assistantActionToolInputSchemas;
